"use client"

import { useState, useCallback } from "react"
import { Eye, EyeOff, X, ArrowLeft, ArrowRight, Sparkles } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { Modal } from "@/components/ui/modal"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useLoginMutation } from "@/app/store/api/authApi"
import { useAppDispatch } from "@/app/store/hooks"
import { setCredentials } from "@/app/store/slices/authSlice"
import { toast } from "sonner"
import Link from "next/link"

interface LoginPopoverProps {
    isOpen: boolean
    onClose: () => void
}

const impactSlides = [
  {
    title: "Start creating polished slides.",
    description: "Because your last pitch deck looked like a basic presentation template. Let our AI handle the design while you focus on delivering your message."
  },
  {
    title: "Format everything with ease.",
    description: "Tired of adjusting layouts instead of building your product? Our AI organizes your unstructured ideas into clear, presentation-ready slides."
  },
  {
    title: "Design expertise not required.",
    description: "And that’s completely fine. Focus on your business strategy while we generate slides that present your work in a clean and professional way."
  },
  {
    title: "Present and secure outcomes.",
    description: "Ready to present with confidence? Export your well-designed decks to PDF with a single click, and engage stakeholders effectively when it matters most."
  }
];

export function LoginPopover({ isOpen, onClose }: LoginPopoverProps) {
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [rememberMe, setRememberMe] = useState(false)
    const [currentSlide, setCurrentSlide] = useState(0)
    const [login, { isLoading }] = useLoginMutation()
    const dispatch = useAppDispatch()

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % impactSlides.length)
    }

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + impactSlides.length) % impactSlides.length)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        try {
            const userData = await login({ email, password }).unwrap()
            dispatch(setCredentials({ user: userData.user, token: userData.token }))
            toast.success("Successfully logged in")
            onClose()
        } catch (err: any) {
            const message = err?.data?.message || err?.message || "Failed to login"
            toast.error(message)
        }
    }

    const handleGoogleLogin = () => {
        window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/auth/google`
    }

    const handleLinkedInLogin = () => {
        window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/auth/linkedin`
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-[800px] w-[95%] p-0 overflow-hidden bg-white rounded-[2rem]">
            <div className="flex flex-col md:flex-row w-full min-h-[500px] bg-white">
                

                {/* Left Side: Impact Content */}
                <div className="hidden md:flex w-[55%] p-3 bg-white relative">
                    <div className="w-full h-full bg-blue-600 rounded-[20px_100px_20px_80px] px-8 pt-8 pb-5 relative flex flex-col justify-between overflow-hidden">
                                               

                        {/* Content */}
                        <div className="relative z-10 max-w-sm mt-2">
                            <div className="h-[210px] w-full relative">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={currentSlide}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.3 }}
                                        className="absolute inset-0 w-full"
                                    >
                                        <h2 className="text-[32px] font-bold text-white mb-4 leading-[1.2] tracking-tight">
                                            {impactSlides[currentSlide].title}
                                        </h2>
                                        
                                        <p className="text-blue-50 text-[15px] leading-relaxed">
                                            {impactSlides[currentSlide].description}
                                        </p>
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                            {/* Arrows */}
                            <div className="flex gap-2 mt-2 relative z-20">
                                <button type="button" onClick={prevSlide} className="w-9 h-9 rounded-full bg-blue-500/40 flex items-center justify-center text-white hover:bg-blue-500/60 transition-all backdrop-blur-sm border border-blue-400/20 cursor-pointer shadow-sm">
                                    <ArrowLeft className="w-4 h-4" />
                                </button>
                                <button type="button" onClick={nextSlide} className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-blue-600 hover:bg-blue-50 transition-all shadow-lg hover:scale-105 active:scale-95 cursor-pointer">
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Overlapping Bottom Card */}
                        <div className="relative z-20 mt-8 w-[108%] -ml-4 bg-white rounded-[30px_80px_30px_60px] p-5 shadow-2xl flex items-center justify-between border border-slate-100">
                            <div>
                                <h3 className="text-slate-900 font-bold text-sm mb-1">Create your first deck</h3>
                                <p className="text-slate-500 text-xs max-w-[250px] leading-snug">Join thousands of founders experiencing the easiest way to build.</p>
                            </div>
                            <div className="flex -space-x-3 mr-2">
                                <img className="w-8 h-8 rounded-full border-2 border-white bg-slate-100 object-cover shadow-sm" src="https://i.pravatar.cc/100?img=1" alt="User 1" />
                                <img className="w-8 h-8 rounded-full border-2 border-white bg-slate-100 object-cover shadow-sm" src="https://i.pravatar.cc/100?img=2" alt="User 2" />
                                <img className="w-8 h-8 rounded-full border-2 border-white bg-slate-100 object-cover shadow-sm" src="https://i.pravatar.cc/100?img=3" alt="User 3" />
                                <div className="w-8 h-8 rounded-full border-2 border-white bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600 shadow-sm z-10">
                                    +2k
                                </div>
                            </div>
                        </div>

                        {/* Decorative background gradients */}
                        <div className="absolute top-1/2 right-[-10%] w-80 h-80 bg-blue-500 rounded-full blur-3xl z-0 opacity-60" />
                        <div className="absolute bottom-[-10%] left-[-10%] w-64 h-64 bg-blue-700 rounded-full blur-3xl z-0 opacity-40" />
                    </div>
                </div>


                {/* Right Side: Login Form */}
                <div className="w-full md:w-[45%] p-6 md:px-8 pt-10 pb-12 flex flex-col justify-center bg-white relative z-10">
                    {/* Header */}
                    <div className="mb-6">
                        <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-1">
                            Welcome back
                        </h2>
                        <p className="text-slate-500 text-xs">
                            Please enter your account details
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="w-full">
                        <div className="mb-3">
                            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email</label>
                            <Input
                                type="email"
                                placeholder="john@example.com"
                                required
                                className="w-full h-10 rounded-lg border border-slate-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-slate-900 placeholder:text-slate-400 text-sm"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>

                        <div className="mb-2">
                            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Password</label>
                            <div className="relative">
                                <Input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    required
                                    className="w-full h-10 px-4 rounded-lg border border-slate-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-slate-900 placeholder:text-slate-400 pr-10 text-sm"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between mb-3 mt-3">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    className="w-3.5 h-3.5 rounded-sm border-slate-300 text-blue-600 focus:ring-blue-500" 
                                    checked={rememberMe} 
                                    onChange={(e) => setRememberMe(e.target.checked)} 
                                />
                                <span className="text-[11px] text-slate-600 font-medium">Remember me</span>
                            </label>
                            <Link href="/forgot-password" className="text-[11px] text-blue-600 hover:text-blue-700 font-semibold transition-colors cursor-pointer">
                                Forgot Password?
                            </Link>
                        </div>

                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full h-10 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-all disabled:opacity-50 cursor-pointer"
                        >
                            {isLoading ? "Signing in..." : "Sign in"}
                        </Button>
                    </form>

                    {/* Social Logins */}
                    <div className="mt-3">
                        <div className="relative flex items-center py-3">
                            <div className="flex-grow border-t border-slate-100"></div>
                            <span className="flex-shrink-0 mx-4 text-slate-400 text-[10px] font-medium tracking-wider">Or continue with</span>
                            <div className="flex-grow border-t border-slate-100"></div>
                        </div>
                        
                        <div className="flex items-center justify-center gap-3 mt-1">
                            <button
                                onClick={handleGoogleLogin}
                                className="w-10 h-10 rounded-lg border border-slate-300 flex items-center justify-center hover:bg-slate-50 transition-all cursor-pointer"
                                aria-label="Sign in with Google"
                            >
                                <svg className="w-4 h-4" viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                </svg>
                            </button>
                            <button
                                onClick={handleLinkedInLogin}
                                className="w-10 h-10 rounded-lg border border-slate-300 flex items-center justify-center hover:bg-slate-50 transition-all cursor-pointer"
                                aria-label="Sign in with LinkedIn"
                            >
                                <svg className="w-4 h-4" fill="#0077b5" viewBox="0 0 24 24">
                                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.761 0 5-2.239 5-5v-14c0-2.761-2.239-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 text-center">
                        <p className="text-xs text-slate-500">
                            Don't have an account? <Link href="/signup" className="text-blue-600 font-semibold hover:text-blue-700 hover:underline transition-colors cursor-pointer">Sign up</Link>
                        </p>
                    </div>

                    <div className="absolute bottom-2 right-3">
                        <Link href="/contact-us" className="text-[10px] text-slate-500 hover:text-slate-700 font-medium transition-colors cursor-pointer">
                            Need help? <span className="text-blue-600 font-semibold hover:underline cursor-pointer">Contact us</span>
                        </Link>
                    </div>
                </div>

                
            </div>
        </Modal>
    )
}
